import { supabase } from "./supabaseClient";

// ─── ACCOUNTS ────────────────────────────────────────────────────────────────

export async function fetchAccounts(userId) {
  const { data, error } = await supabase
    .from("accounts")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: true });
  if (error) { console.error("fetchAccounts error:", error); return null; }
  return data.map(dbToAccount);
}

export async function saveAccount(userId, account) {
  const row = accountToDb(userId, account);
  const { error } = await supabase
    .from("accounts")
    .upsert(row, { onConflict: "user_id,local_id" });
  if (error) console.error("saveAccount error:", error);
}

export async function deleteAccount(userId, localId) {
  const { error } = await supabase
    .from("accounts")
    .delete()
    .eq("user_id", userId)
    .eq("local_id", String(localId));
  if (error) console.error("deleteAccount error:", error);
}

export async function saveAllAccounts(userId, accounts) {
  if (!accounts.length) return;
  const rows = accounts.map(a => accountToDb(userId, a));
  const { error } = await supabase
    .from("accounts")
    .upsert(rows, { onConflict: "user_id,local_id" });
  if (error) console.error("saveAllAccounts error:", error);
}

// ─── PAYMENTS ─────────────────────────────────────────────────────────────────

export async function fetchPayments(userId) {
  const { data, error } = await supabase
    .from("payments")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) { console.error("fetchPayments error:", error); return null; }

  // Group by account_local_id to match the allPayments shape: { [accountId]: [payments] }
  const grouped = {};
  data.forEach(p => {
    const key = p.account_local_id;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(dbToPayment(p));
  });
  return grouped;
}

export async function savePayment(userId, accountLocalId, payment) {
  const row = paymentToDb(userId, accountLocalId, payment);
  const { error } = await supabase
    .from("payments")
    .upsert(row, { onConflict: "user_id,local_id" });
  if (error) console.error("savePayment error:", error);
}

export async function deletePayment(userId, localId) {
  const { error } = await supabase
    .from("payments")
    .delete()
    .eq("user_id", userId)
    .eq("local_id", String(localId));
  if (error) console.error("deletePayment error:", error);
}

export async function saveAllPayments(userId, allPayments) {
  const rows = [];
  Object.entries(allPayments).forEach(([accountLocalId, payments]) => {
    payments.forEach(p => rows.push(paymentToDb(userId, accountLocalId, p)));
  });
  if (!rows.length) return;
  const { error } = await supabase
    .from("payments")
    .upsert(rows, { onConflict: "user_id,local_id" });
  if (error) console.error("saveAllPayments error:", error);
}

// ─── SETTINGS ─────────────────────────────────────────────────────────────────

export async function fetchSettings(userId) {
  const { data, error } = await supabase
    .from("settings")
    .select("*")
    .eq("user_id", userId)
    .single();
  if (error && error.code !== "PGRST116") { // PGRST116 = no rows found
    console.error("fetchSettings error:", error);
    return null;
  }
  if (!data) return null;
  return {
    strategy: data.strategy || "avalanche",
    extraBudget: data.extra_budget || 0,
    customOrder: data.custom_order || [],
    easyMode: data.easy_mode || false,
    monthOverrides: data.month_overrides || {},
    extraSchedule: data.extra_schedule || [],
    monthlyPayments: data.monthly_payments || {},
    monthlyPaymentsHistory: data.monthly_payments_history || [],
    seenMilestones: data.seen_milestones || [],
    isPro: data.is_pro || false,
  };
}

export async function saveSettings(userId, settings) {
  const { error } = await supabase
    .from("settings")
    .upsert({
      user_id: userId,
      strategy: settings.strategy,
      extra_budget: settings.extraBudget,
      custom_order: settings.customOrder,
      easy_mode: settings.easyMode,
      month_overrides: settings.monthOverrides,
      extra_schedule: settings.extraSchedule,
      monthly_payments: settings.monthlyPayments,
      monthly_payments_history: settings.monthlyPaymentsHistory,
      seen_milestones: settings.seenMilestones,
      updated_at: new Date().toISOString(),
    }, { onConflict: "user_id" });
  if (error) console.error("saveSettings error:", error);
}

// ─── CONVERTERS ───────────────────────────────────────────────────────────────

function accountToDb(userId, account) {
  return {
    user_id: userId,
    local_id: String(account.id),
    name: account.name,
    type: account.type,
    balance: account.balance,
    rate: account.rate,
    min_payment: account.minPayment,
    payment_date: account.paymentDate ? String(account.paymentDate) : null,
    last_payment_date: account.lastPaymentDate || null,
    original_last_payment_date: account.originalLastPaymentDate || null,
    previous_last_payment_date: account.previousLastPaymentDate || null,
    updated_at: new Date().toISOString(),
  };
}

function dbToAccount(row) {
  return {
    id: isNaN(row.local_id) ? row.local_id : Number(row.local_id),
    name: row.name,
    type: row.type,
    balance: row.balance,
    rate: row.rate,
    minPayment: row.min_payment,
    paymentDate: row.payment_date || null,
    lastPaymentDate: row.last_payment_date || null,
    originalLastPaymentDate: row.original_last_payment_date || null,
    previousLastPaymentDate: row.previous_last_payment_date || null,
  };
}

function paymentToDb(userId, accountLocalId, payment) {
  return {
    user_id: userId,
    account_local_id: String(accountLocalId),
    local_id: String(payment.id),
    amount: payment.amount,
    principal: payment.principal || 0,
    interest: payment.interest || 0,
    fees: payment.fees || 0,
    date: payment.date,
    type: payment.type || "Minimum Payment",
    notes: payment.notes || null,
    saved_interest: payment.savedInterest || 0,
    months_reduced: payment.monthsReduced || 0,
  };
}

function dbToPayment(row) {
  return {
    id: isNaN(row.local_id) ? row.local_id : Number(row.local_id),
    amount: row.amount,
    principal: row.principal,
    interest: row.interest,
    fees: row.fees,
    date: row.date,
    type: row.type,
    notes: row.notes,
    savedInterest: row.saved_interest != null ? row.saved_interest : null,
    monthsReduced: row.months_reduced != null ? row.months_reduced : null,
  };
}
